import React, { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useToggle } from 'react-use';
import {
    selectIsFromProducts,
    selectIsFromSearchApp,
    selectIsFromYaRu,
} from '../data-layer/meta/selectors';
import { useSelector } from '../data-layer/useSelector';
import { AppDiscoveryMobile } from '../ui/discovery/AppDiscoveryMobile';
import { getIsMobile } from '../utils/getIsMobile';
import { getWindow } from '../utils/getWindow';
import { useIsSomeDialogOpen } from './ModalRoute';

const ANIMATE_TIMEOUT = 200;
const APP_DISCOVERY_DELAY = 7000;
const APP_DISCOVERY_PAUSE = 24 * 60 * 60 * 1000;
const APP_DISCOVERY_RESET_TIME_LS_KEY = 'app-discovery-reset-time';
const APP_DISCOVERY_SHOWS_COUNT_LS_KEY = 'app-discovery-shows-count';

const excludedPages = [
    '/businesses/edit',
    '/businesses/registration',
    '/partner/registration',
    '/management',
    '/products',
    '/catalog',
];

export const AppDiscovery = () => {
    const [isVisible, toggleIsVisible] = useToggle(false);
    const isSomeDialogOpen = useIsSomeDialogOpen();
    const { isMobile } = getIsMobile();
    const { pathname } = useLocation();
    const shouldIgnore = excludedPages.some((page) => pathname.includes(page));

    const isSomeDialogOpenRef = useRef(isSomeDialogOpen);
    const toggleIsVisibleRef = useRef(toggleIsVisible);
    const timerStartedRef = useRef(false);
    const isFromSearchApp = useSelector(selectIsFromSearchApp);
    const isFromProducts = useSelector(selectIsFromProducts);
    const isFromYaRu = useSelector(selectIsFromYaRu);
    const isFromExternalPage = isFromSearchApp || isFromProducts || isFromYaRu;

    useEffect(() => {
        toggleIsVisibleRef.current = toggleIsVisible;
        isSomeDialogOpenRef.current = isSomeDialogOpen;
    }, [toggleIsVisible, isSomeDialogOpen]);

    useEffect(() => {
        if (timerStartedRef.current || isFromExternalPage === undefined) {
            return;
        }

        timerStartedRef.current = true;
        if (isMobile && !shouldIgnore && !isFromExternalPage) {
            setTimeout(() => {
                const currentTime = new Date().getTime();
                const resetTime = parseInt(
                    getWindow()?.localStorage.getItem(APP_DISCOVERY_RESET_TIME_LS_KEY) || '0',
                    10,
                );
                const showsCount = parseInt(
                    getWindow()?.localStorage.getItem(APP_DISCOVERY_SHOWS_COUNT_LS_KEY) || '0',
                    10,
                );

                if (!isSomeDialogOpenRef.current && currentTime > resetTime && showsCount < 3) {
                    toggleIsVisibleRef.current();
                    getWindow()?.localStorage.setItem(
                        APP_DISCOVERY_RESET_TIME_LS_KEY,
                        String(currentTime + APP_DISCOVERY_PAUSE),
                    );
                    getWindow()?.localStorage.setItem(
                        APP_DISCOVERY_SHOWS_COUNT_LS_KEY,
                        String(showsCount + 1),
                    );
                }
            }, APP_DISCOVERY_DELAY);
        }
    }, [isFromExternalPage, isMobile, shouldIgnore]);

    const handleClose = useCallback(() => {
        getWindow()?.setTimeout(() => {
            toggleIsVisible();
        }, ANIMATE_TIMEOUT);
    }, [toggleIsVisible]);

    if (!isVisible) {
        return null;
    }

    return <AppDiscoveryMobile onClose={handleClose} timeout={ANIMATE_TIMEOUT} />;
};
